import React, { useState } from 'react';
import Select from 'react-select';
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Module {
    value: string;
    label: string;
}

interface TrialFormProps {
    onSuccess: () => void;
}

const TrialForm: React.FC<TrialFormProps> = ({ onSuccess }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [modules, setModules] = useState<Module[]>([]);
    const [loading, setLoading] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const options: Module[] = [
        { value: 'hr', label: 'Human Resources' },
        { value: 'sales', label: 'Sales' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance' },
        { value: 'legal', label: 'Legal' },
        { value: 'performance', label: 'Performance' },
        { value: 'projects', label: 'Projects' },
    ];

    // Check if emails match
    const emailsMatch = email === confirmEmail;
    const showEmailError = confirmEmail.length > 0 && !emailsMatch;
    const isSubmitDisabled = loading || !name.trim() || !email.trim() || !confirmEmail.trim() || !emailsMatch;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        if (!name.trim() || !email.trim() || !confirmEmail.trim()) {
            alert('Please fill in all fields before submitting.');
            setLoading(false);
            return;
        }

        if (email !== confirmEmail) {
            alert('Email confirmation does not match.');
            setLoading(false);
            return;
        }

        try {
            const selectedModules = modules.map(m => m.label).join(', ');
            const message = `Trial Request from ${name}\n\nEmail: ${email}\nInterested Modules: ${selectedModules || 'None selected'}\n\nPlease set up a trial account for this user.`;

            const { data, error } = await supabase.functions.invoke('send-contact-email', {
                body: {
                    name,
                    email,
                    message
                }
            });

            if (error) {
                throw error;
            }

            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Error sending trial request:', error);
            alert('Failed to send trial request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        onSuccess();
    };

    return (
        <>
            <div className="trial-form space-y-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="text"
                        placeholder="Provide your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="text-[12px] w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                    />
                    <input
                        type="email"
                        placeholder="Provide your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="text-[12px] w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                    />
                    <div className="space-y-1">
                        <input
                            type="email"
                            placeholder="Confirm your email address"
                            value={confirmEmail}
                            onChange={(e) => setConfirmEmail(e.target.value)}
                            className="text-[12px] w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                        {showEmailError && (
                            <p className="text-red-500 text-[11px] mt-1">Email address does not match</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="block text-[15px] text-black font-medium">
                            Select the modules
                        </label>
                        <Select
                            isMulti
                            options={options}
                            onChange={(newValue) => setModules(newValue as Module[])}
                            value={modules}
                            className="react-select-container text-[12px]"
                            classNamePrefix="react-select"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    padding: '4px',
                                    text: '12px',
                                }),
                            }}
                        />
                    </div>
                    <div className="flex justify-end">
                        <button 
                            type="submit" 
                            disabled={isSubmitDisabled}
                            className="w-[150px] text-[14px] bg-primary text-black py-3 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                        {loading ? 'Submitting...' : 'Submit Request'}
                        </button>
                    </div>
                </form>
            </div>

            <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Trial Request Sent Successfully!</AlertDialogTitle>
                        <AlertDialogDescription>
                            We have received your trial request and will contact you soon.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={handleSuccessDialogClose}>
                            OK
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default TrialForm;
